import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import imageCompression from 'browser-image-compression';
import { getApiBaseUrl } from '../../config/api';
import FitCoverImage from '../shared/FitCoverImage';
import { isEmployer } from '../../utils/userRole';
import { formatEventPlace, formatEventWhen } from './calendarLinks';
import { downloadConfirmedAttendees } from './downloadAttendees';
import './EventAttendancePanel.css';

const emptyForm = {
  title: '',
  description: '',
  event_date: '',
  start_time: '',
  end_time: '',
  location: '',
  country: '',
  organizer: '',
};

function resolveCoverPreview(cover, apiUrl) {
  if (!cover) return null;
  const raw = String(cover).trim();
  if (!raw) return null;
  if (/^(https?:|blob:|data:)/i.test(raw)) return raw;
  const base = String(apiUrl || '').replace(/\/+$/, '');
  return `${base}/${raw.replace(/^\/+/, '')}`;
}

function coverUploadName(file, fallback = 'cover.jpg') {
  const raw = String((file && file.name) || fallback);
  const ext = raw.split('.').pop()?.toLowerCase();
  const allowed = ['jpg', 'jpeg', 'jpe', 'jfif', 'png', 'gif', 'webp', 'bmp', 'avif'];
  if (ext && allowed.includes(ext)) {
    return raw.replace(/[^\w.\-]+/g, '_') || fallback;
  }
  const mime = String((file && file.type) || '');
  const fromMime = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'image/gif': 'gif',
    'image/bmp': 'bmp',
    'image/avif': 'avif',
  };
  return `cover.${fromMime[mime] || 'jpg'}`;
}

async function parseJsonResponse(res) {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(
      res.status >= 400
        ? 'Server rejected the upload. The cover photo may be too large.'
        : 'Server returned an invalid response while saving.'
    );
  }
}

export default function EventAttendancePanel({ user, onClose }) {
  const API_URL = getApiBaseUrl();
  const [tab, setTab] = useState('create');
  const [form, setForm] = useState(emptyForm);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [attendeesByEvent, setAttendeesByEvent] = useState({});
  const [downloadingId, setDownloadingId] = useState(null);
  const previewRef = useRef(null);

  const canManage = isEmployer(user);

  useEffect(() => {
    return () => {
      if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    };
  }, []);

  const loadMine = useCallback(async () => {
    if (!user?.id || !canManage) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/get_attendance_events.php?mine=1&user_id=${user.id}&include_drafts=1`
      );
      const data = await res.json();
      setEvents(data.success && Array.isArray(data.events) ? data.events : []);
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [API_URL, user?.id, canManage]);

  useEffect(() => {
    if (tab === 'manage') loadMine();
  }, [tab, loadMine]);

  if (!canManage) {
    return (
      <div className="eap-root">
        <p className="eap-empty">Only Employers and Admins can create Event Attendance posts.</p>
      </div>
    );
  }

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const clearCoverPreview = () => {
    if (previewRef.current) {
      URL.revokeObjectURL(previewRef.current);
      previewRef.current = null;
    }
  };

  const resetForm = () => {
    setForm(emptyForm);
    setCoverFile(null);
    clearCoverPreview();
    setCoverPreview(null);
    setEditingId(null);
  };

  const handleCoverChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'err', text: 'Cover must be an image file.' });
      return;
    }
    clearCoverPreview();
    const url = URL.createObjectURL(file);
    previewRef.current = url;
    setCoverFile(file);
    setCoverPreview(url);
    setMessage(null);
  };

  const startEdit = (event) => {
    setEditingId(event.id);
    setForm({
      title: event.title || '',
      description: event.description || '',
      event_date: String(event.event_date || '').slice(0, 10),
      start_time: String(event.start_time || '').slice(0, 5),
      end_time: String(event.end_time || '').slice(0, 5),
      location: event.location || '',
      country: event.country || '',
      organizer: event.organizer || '',
    });
    setCoverFile(null);
    clearCoverPreview();
    setCoverPreview(resolveCoverPreview(event.cover_image, API_URL));
    setTab('create');
    setMessage(null);
  };

  const submit = async (status) => {
    if (!form.title.trim() || !form.event_date) {
      setMessage({ type: 'err', text: 'Title and date are required.' });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const fd = new FormData();
      fd.append('user_id', String(user.id));
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append('status', status);
      if (editingId) fd.append('event_id', String(editingId));

      if (coverFile) {
        try {
          const compressed = await imageCompression(coverFile, {
            maxSizeMB: 1.5,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
          });
          fd.append('cover_image', compressed, coverUploadName(compressed, coverUploadName(coverFile)));
        } catch {
          fd.append('cover_image', coverFile, coverUploadName(coverFile));
        }
      }

      const endpoint = editingId ? 'update_attendance_event.php' : 'create_attendance_event.php';
      const res = await fetch(`${API_URL}/${endpoint}`, { method: 'POST', body: fd });
      const data = await parseJsonResponse(res);
      if (!data.success) {
        setMessage({ type: 'err', text: data.message || 'Could not save event.' });
        return;
      }
      setMessage({
        type: 'ok',
        text: data.message || (status === 'published' ? 'Event published.' : 'Draft saved.'),
      });
      resetForm();
      if (tab === 'manage') loadMine();
      else setTab('manage');
    } catch (err) {
      setMessage({
        type: 'err',
        text: err?.message || 'Network error while saving.',
      });
    } finally {
      setSaving(false);
    }
  };

  const removeEvent = async (eventId) => {
    if (!window.confirm('Delete this event and all RSVPs?')) return;
    try {
      const res = await fetch(`${API_URL}/delete_attendance_event.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, event_id: eventId }),
      });
      const data = await res.json();
      if (data.success) {
        setEvents((prev) => prev.filter((e) => e.id !== eventId));
      } else {
        alert(data.message || 'Delete failed');
      }
    } catch {
      alert('Network error');
    }
  };

  const loadAttendees = async (eventId) => {
    if (attendeesByEvent[eventId]) {
      setAttendeesByEvent((prev) => {
        const next = { ...prev };
        delete next[eventId];
        return next;
      });
      return;
    }
    try {
      const res = await fetch(
        `${API_URL}/get_attendance_event_attendees.php?event_id=${eventId}&user_id=${user.id}`
      );
      const data = await res.json();
      setAttendeesByEvent((prev) => ({
        ...prev,
        [eventId]: data.success ? data.attendees || [] : [],
      }));
    } catch {
      setAttendeesByEvent((prev) => ({ ...prev, [eventId]: [] }));
    }
  };

  const downloadAttendees = async (event) => {
    setDownloadingId(event.id);
    try {
      await downloadConfirmedAttendees({
        apiUrl: API_URL,
        event,
        userId: user.id,
      });
    } catch (err) {
      alert(err?.message || 'Could not download attendees.');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="eap-root">
      <div className="eap-header">
        <h2>Attend Events</h2>
        <p>
          Create standalone events members can RSVP to and save on their calendar. Separate from
          Series of Events.
        </p>
      </div>

      <div className="eap-tabs" role="tablist">
        <button
          type="button"
          className={`eap-tab${tab === 'create' ? ' is-active' : ''}`}
          onClick={() => setTab('create')}
        >
          {editingId ? 'Edit event' : 'Create event'}
        </button>
        <button
          type="button"
          className={`eap-tab${tab === 'manage' ? ' is-active' : ''}`}
          onClick={() => setTab('manage')}
        >
          Manage
        </button>
      </div>

      {message && (
        <div className={`eap-msg eap-msg--${message.type === 'ok' ? 'ok' : 'err'}`}>
          {message.text}
        </div>
      )}

      {tab === 'create' && (
        <form
          className="eap-form"
          onSubmit={(e) => {
            e.preventDefault();
            submit('published');
          }}
        >
          <div className="eap-field">
            <label htmlFor="eap-title">Event title</label>
            <input
              id="eap-title"
              name="title"
              value={form.title}
              onChange={onChange}
              placeholder="Workshop title"
              required
            />
          </div>
          <div className="eap-field">
            <label htmlFor="eap-desc">Description</label>
            <textarea
              id="eap-desc"
              name="description"
              value={form.description}
              onChange={onChange}
              placeholder="What attendees should know…"
            />
          </div>
          <div className="eap-row eap-row--3">
            <div className="eap-field">
              <label htmlFor="eap-date">Date</label>
              <input
                id="eap-date"
                type="date"
                name="event_date"
                value={form.event_date}
                onChange={onChange}
                required
              />
            </div>
            <div className="eap-field">
              <label htmlFor="eap-start">Start time</label>
              <input
                id="eap-start"
                type="time"
                name="start_time"
                value={form.start_time}
                onChange={onChange}
              />
            </div>
            <div className="eap-field">
              <label htmlFor="eap-end">End time</label>
              <input
                id="eap-end"
                type="time"
                name="end_time"
                value={form.end_time}
                onChange={onChange}
              />
            </div>
          </div>
          <div className="eap-row eap-row--2">
            <div className="eap-field">
              <label htmlFor="eap-location">Location</label>
              <input
                id="eap-location"
                name="location"
                value={form.location}
                onChange={onChange}
                placeholder="Venue or online link"
              />
            </div>
            <div className="eap-field">
              <label htmlFor="eap-country">Country</label>
              <input
                id="eap-country"
                name="country"
                value={form.country}
                onChange={onChange}
                placeholder="Optional"
              />
            </div>
          </div>
          <div className="eap-field">
            <label htmlFor="eap-organizer">Organizer</label>
            <input
              id="eap-organizer"
              name="organizer"
              value={form.organizer}
              onChange={onChange}
              placeholder="Host name or company"
            />
          </div>
          <div className="eap-field">
            <label htmlFor="eap-cover">Cover image (optional)</label>
            <input
              id="eap-cover"
              type="file"
              accept="image/*"
              onChange={handleCoverChange}
            />
            <p className="eap-hint">JPG, PNG, GIF, or WebP. Large photos are compressed automatically.</p>
            <FitCoverImage
              src={coverPreview}
              alt="Event cover preview"
              maxHeight={240}
              radius={10}
              marginBottom={0}
            />
          </div>
          <div className="eap-actions">
            <button type="submit" className="eap-btn eap-btn--primary" disabled={saving}>
              {saving ? 'Saving…' : editingId ? 'Update & publish' : 'Publish event'}
            </button>
            <button
              type="button"
              className="eap-btn eap-btn--secondary"
              disabled={saving}
              onClick={() => submit('draft')}
            >
              Save draft
            </button>
            {editingId && (
              <button type="button" className="eap-btn eap-btn--secondary" onClick={resetForm}>
                Cancel edit
              </button>
            )}
            {onClose && (
              <button type="button" className="eap-btn eap-btn--secondary" onClick={onClose}>
                Close
              </button>
            )}
          </div>
        </form>
      )}

      {tab === 'manage' && (
        <div className="eap-list">
          {loading ? (
            <p className="eap-empty">Loading…</p>
          ) : events.length === 0 ? (
            <p className="eap-empty">No attendance events yet. Create one to get started.</p>
          ) : (
            events.map((event) => (
              <div key={event.id} className="eap-card">
                <div>
                  <h3>
                    {event.title}
                    <span
                      className={`eap-badge eap-badge--${
                        event.status === 'published' ? 'published' : 'draft'
                      }`}
                    >
                      {event.status || 'draft'}
                    </span>
                  </h3>
                  <div className="eap-meta">{formatEventWhen(event)}</div>
                  {formatEventPlace(event) && (
                    <div className="eap-meta">{formatEventPlace(event)}</div>
                  )}
                  <div className="eap-meta">
                    {Number(event.attendee_count || 0)} attending
                  </div>
                </div>
                <div className="eap-card-actions">
                  <Link
                    to={`/event-attendance/${event.id}`}
                    className="eap-btn eap-btn--secondary"
                    style={{ textDecoration: 'none' }}
                  >
                    View
                  </Link>
                  <button type="button" className="eap-btn eap-btn--secondary" onClick={() => startEdit(event)}>
                    Edit
                  </button>
                  <button
                    type="button"
                    className="eap-btn eap-btn--secondary"
                    onClick={() => loadAttendees(event.id)}
                  >
                    {attendeesByEvent[event.id] ? 'Hide attendees' : 'Attendees'}
                  </button>
                  <button
                    type="button"
                    className="eap-btn eap-btn--secondary"
                    disabled={downloadingId === event.id || Number(event.attendee_count || 0) === 0}
                    onClick={() => downloadAttendees(event)}
                    title={
                      Number(event.attendee_count || 0) === 0
                        ? 'No confirmed attendees yet'
                        : 'Download confirmed attendees as CSV'
                    }
                  >
                    {downloadingId === event.id ? 'Downloading…' : 'Download attendees'}
                  </button>
                  <button
                    type="button"
                    className="eap-btn eap-btn--danger"
                    onClick={() => removeEvent(event.id)}
                  >
                    Delete
                  </button>
                </div>
                {attendeesByEvent[event.id] && (
                  <div className="eap-attendees">
                    {attendeesByEvent[event.id].length === 0 ? (
                      <p className="eap-meta">No attendees yet.</p>
                    ) : (
                      <ul>
                        {attendeesByEvent[event.id].map((a) => (
                          <li key={a.id}>
                            {a.name || 'Member'}
                            {a.email ? ` · ${a.email}` : ''}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
